/**
 * Shopify read hooks. Status is brand-wide (the BE resolves the caller's brand
 * from context); conversions are campaign-scoped.
 */
import { useRef } from 'react';
import { keepPreviousData, useInfiniteQuery, useQuery, skipToken } from '@tanstack/react-query';
import type {
  ShopifyConversionsParams,
  ShopifyConversionsRange,
  ShopifyCouponMetrics,
  ShopifyRefusedParams,
  ShopifyStoreDiscountFilters,
} from '@discovr/types';
import { createShopifyQueriesApi } from '../api/shopifyQueries';
import { shopifyKeys } from '../queryKeys';
import { summaryPollInterval } from './shopifySetupProgress';

const api = createShopifyQueriesApi();

export const SHOPIFY_STATUS_POLL_MS = 4000;

/** Lifecycle states that end on their own, so the card must keep asking. */
const TRANSIENT_STATES: ReadonlySet<string> = new Set(['uninstalling', 'deleting']);

/** `enabled: false` for callers the brand-only endpoint 403s (the admin lane). */
export function useShopifyStatus({ enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: shopifyKeys.status(),
    queryFn: () => api.getStatus(),
    enabled,
    staleTime: 5 * 60_000,
    // 'always', not true: the merchant uninstalls in Shopify admin and comes
    // back inside the 5 min staleTime, where `true` would skip the refetch.
    refetchOnWindowFocus: 'always',
    refetchInterval: (query) =>
      TRANSIENT_STATES.has(query.state.data?.lifecycle_state ?? '')
        ? SHOPIFY_STATUS_POLL_MS
        : false,
  });
}

/** Resolves which store a post-install claim token would bind, so the brand can
 *  confirm before anything is committed. Never retried: the failure modes are
 *  403/404/409/410 on the token itself, where a retry only delays the error —
 *  the dialog offers an explicit retry for the rest.
 *
 *  gcTime 0 keeps a bearer token out of the cache and the devtools. No
 *  focus refetch: the merchant has just come back from the Shopify tab, and a
 *  transient failure there would flip a resolved dialog into its error state
 *  with the store name already on screen. skipToken over a cast, so a stray
 *  refetch() cannot send `?token=null`. */
export function useShopifyClaimPreview(token: string | null) {
  return useQuery({
    queryKey: shopifyKeys.claimPreview(token ?? ''),
    queryFn: token ? () => api.previewClaim(token) : skipToken,
    retry: false,
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
    // Same reason as focus: a reconnect refetch that fails would flip a
    // resolved dialog into its error state with the store name already shown.
    refetchOnReconnect: false,
  });
}

/** Live store summary (shop name + currency + product count via the Admin API).
 *  Pass `enabled` (typically the connected flag) so it only fires once a store
 *  is bound. The `store` block is best-effort — absent on a transient pull. */
export function useShopifyStoreSummary(enabled = false) {
  return useQuery({
    queryKey: shopifyKeys.summary(),
    queryFn: () => api.getSummary(),
    enabled,
    staleTime: 5 * 60_000,
  });
}

/** Brand-wide at-a-glance figures for the Integrations card. No retry: a failure
 *  hides the row, and it should do so promptly. */
export function useShopifyGlance(enabled = false) {
  return useQuery({
    queryKey: shopifyKeys.glance(),
    queryFn: () => api.getGlance(),
    enabled,
    retry: false,
    staleTime: 60_000,
  });
}

/** One page of one conversions table (a scope, active or revoked codes). The
 *  previous page stays visible while the next loads. */
export function useShopifyConversionsPage<T extends ShopifyCouponMetrics = ShopifyCouponMetrics>(
  campaignId: string,
  params: ShopifyConversionsParams,
  enabled = true,
) {
  return useQuery({
    queryKey: shopifyKeys.conversionsPage(campaignId, params),
    queryFn: () => api.getConversionsPage<T>(campaignId, params),
    enabled: enabled && !!campaignId,
    staleTime: 60_000,
    refetchOnWindowFocus: 'always',
    placeholderData: keepPreviousData,
  });
}

/** Tiles, connection state and table counts over every row of both scopes. */
export function useShopifyConversionsSummary(
  campaignId: string,
  enabled = true,
  range: ShopifyConversionsRange = {},
  poll = false,
) {
  const pollStartedAt = useRef<{ campaignId: string; at: number } | null>(null);
  return useQuery({
    queryKey: shopifyKeys.conversionsSummary(campaignId, range),
    queryFn: () => api.getConversionsSummary(campaignId, range),
    enabled: enabled && !!campaignId,
    staleTime: 60_000,
    refetchOnWindowFocus: 'always',
    // Hidden tabs are paused by the default refetchIntervalInBackground: false.
    refetchInterval: poll
      ? (query) => {
          const now = Date.now();
          if (pollStartedAt.current?.campaignId !== campaignId) {
            pollStartedAt.current = { campaignId, at: now };
          }
          return summaryPollInterval(query.state.data, pollStartedAt.current.at, now);
        }
      : false,
    // Switching range keeps the last figures on screen, but never another campaign's.
    placeholderData: (prev, prevQuery) =>
      prevQuery?.queryKey[2] === campaignId ? prev : undefined,
  });
}

/** Weekly net sales / orders / visits for the trend chart. */
export function useShopifyConversionsSeries(
  campaignId: string,
  enabled = true,
  range: ShopifyConversionsRange = {},
) {
  return useQuery({
    queryKey: shopifyKeys.conversionsSeries(campaignId, range),
    queryFn: () => api.getConversionsSeries(campaignId, range),
    enabled: enabled && !!campaignId,
    staleTime: 60_000,
    refetchOnWindowFocus: 'always',
    placeholderData: (prev, prevQuery) =>
      prevQuery?.queryKey[2] === campaignId ? prev : undefined,
  });
}

/** Orders that used a Discovr code or link but were not credited. */
export function useShopifyRefusedOrders(
  campaignId: string,
  params: ShopifyRefusedParams,
  enabled = true,
) {
  return useQuery({
    queryKey: shopifyKeys.conversionsRefused(campaignId, params),
    queryFn: () => api.getRefusedOrders(campaignId, params),
    enabled: enabled && !!campaignId,
    staleTime: 60_000,
    refetchOnWindowFocus: 'always',
    placeholderData: keepPreviousData,
  });
}

/** What uninstalling deletes (campaigns, coupon/conversion/click counts) —
 *  backs the uninstall dialog. Pass `enabled` so it only fires once the dialog
 *  is reachable; disabled leaves `data` undefined rather than fetching. */
/** Every discount in the connected store, owner-only on the BE (403 otherwise).
 *  Cursor-paged; `enabled` keeps non-owners from ever asking. */
export function useShopifyStoreDiscounts(enabled = false, filters: ShopifyStoreDiscountFilters = {}) {
  return useInfiniteQuery({
    queryKey: shopifyKeys.storeDiscounts(filters),
    queryFn: ({ pageParam }: { pageParam: string | undefined }) =>
      api.getStoreDiscounts(pageParam, filters),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    enabled,
    staleTime: 30_000,
    retry: false,
    // Rows of the other source must never stand in for this one's.
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[2]?.source === filters.source ? previous : undefined,
  });
}

/** Campaigns that have stored Discovr discounts, for the Store discounts campaign filter. */
export function useShopifyDiscountCampaigns(enabled = false) {
  return useQuery({
    queryKey: shopifyKeys.storeDiscountCampaigns(),
    queryFn: () => api.getStoreDiscountCampaigns(),
    enabled,
    staleTime: 30_000,
    retry: false,
  });
}

export function useShopifyUninstallImpact(enabled = false) {
  return useQuery({
    queryKey: shopifyKeys.uninstallImpact(),
    queryFn: () => api.getUninstallImpact(),
    enabled,
    staleTime: 30_000,
  });
}
